import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom';

import { Box, Button, Tab, Tabs } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import FlagIcon from '@material-ui/icons/Flag';
import { TEACHERS_PERMISSION_DENIED_URL } from 'config/urls';
import { TEACHERS_STUDENTS_DETAIL } from 'config/urls';
import { getStudentGoalCsvExportURL, useSectionDetail, useStudent } from 'sdk';
import { cutText } from 'utils';

import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import GoalsSearchAndFilter from 'pages/Teachers/StudentDetail/components/GoalsSearchAndFilter';
import GoalsTable from 'pages/Teachers/StudentDetail/components/GoalsTable';
import StudentSubtitle from 'pages/Teachers/StudentDetail/components/StudentSubtitle';
import { ReactComponent as BackIcon } from 'pages/Teachers/StudentListV2/assets/back-icon.svg';
import StudentsPortfolio from 'pages/Teachers/StudentPortfolio';
import { useNavbar } from 'pages/Teachers/shared';
import AddGoalDialog from 'pages/Teachers/shared/AddGoalDialog';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { openInNewTab } from 'utils/urls';

import ExportButton from 'components/ExportButton';
import UserContext from 'components/UserContext';

import { ReactComponent as PortfolioIcon } from './assets/portfolio.svg';
import styles from './styles.module.scss';

const ELA_INSIGHTS_ENABLED =
  process.env.REACT_APP_ELA_INSIGHTS_ENABLED === 'true';

const StudentDetail = () => {
  const user = useContext(UserContext);
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const { studentId, sectionId } = match.params;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [goalName, setGoalName] = useState('');
  const [filters, setFilters] = useState({
    progress: '',
    standards: []
  });

  const { data: studentDetails, refetch: refetchStudentDetails } = useStudent({
    studentId
  });

  const {
    data: sectionDetail,
    isLoading: isSectionDetailLoading,
    refetch: refetchSectionDetail
  } = useSectionDetail({
    sectionId: match.params.sectionId
  });

  useEffect(() => {
    if (!sectionDetail) return;
    if (activeTabIndex === 0 && sectionDetail?.is_math) {
      trackMixpanelEvent(user.id, 'User views goals page', {
        feature: 'students',
        view: 'goals',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
      });
    }
    if (
      activeTabIndex === 1 ||
      (activeTabIndex === 0 && !sectionDetail?.is_math)
    ) {
      trackMixpanelEvent(user.id, 'User views portfolio page', {
        feature: 'students',
        view: 'portfolio',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
      });
    }
  }, [activeTabIndex, sectionDetail, user.id]);

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title="Student Goal Progress"
          subtitle={
            <StudentSubtitle
              studentName={studentDetails?.name}
              sectionLabel={cutText({ text: sectionDetail?.name, char: 50 })}
              fullLabel={sectionDetail?.name}
              subjectName={sectionDetail?.subject?.name}
              sectionId={sectionDetail?.id}
              refetchSectionDetail={refetchSectionDetail}
            />
          }
          Icon={BackIcon}
        />
      ),
      isTransparent: true
    }),
    [
      studentDetails?.name,
      sectionDetail?.name,
      sectionDetail?.subject?.name,
      refetchSectionDetail,
      sectionDetail?.id
    ]
  );

  useNavbar(navConfig);

  const handleExportResults = useCallback(() => {
    openInNewTab(getStudentGoalCsvExportURL({ sectionId, studentId }));
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'students',
        view: 'goals',
        button: 'export data',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
      }
    );
  }, [user.id, sectionId, studentId]);

  if (user.is_in_free_trial) {
    return <Redirect to={TEACHERS_PERMISSION_DENIED_URL} />;
  }

  const handleTabChange = ({ newValue }) => {
    setActiveTabIndex(newValue);

    if (newValue === 0 && location.search) {
      history.replace({
        pathname: location.pathname,
        state: location.state
      });
    }
  };

  const handleAddGoalClick = () => {
    setIsGoalDialogOpen(true);
    trackMixpanelEvent(user.id, 'User starts to add a goal', {
      feature: 'students',
      view: 'goals',
      button: 'add',
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
    });
  };

  const handleGoalAdd = () => {
    refetchStudentDetails();
    setIsGoalDialogOpen(false);
  };

  const handleFiltersReset = () => {
    setFilters({
      progress: '',
      standards: []
    });
  };

  return (
    <>
      {isGoalDialogOpen && (
        <AddGoalDialog
          isOpen={isGoalDialogOpen}
          onClose={() => {
            setIsGoalDialogOpen(false);
          }}
          onAdd={handleGoalAdd}
          studentId={studentId}
        />
      )}
      <div className={styles.studentDetailWrapper}>
        <div className={styles.studentDetailContainer}>
          {isSectionDetailLoading ? (
            <div className={styles.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <div className={styles.studentDetailHeader}>
                <Tabs
                  value={activeTabIndex}
                  onChange={(event, value) => {
                    handleTabChange({ newValue: value });
                  }}
                  aria-label="student detail tabs"
                  indicatorColor="inherit"
                  textColor="inherit"
                  classes={{ indicator: styles.customTabIndicator }}
                >
                  {(sectionDetail?.is_math ||
                    (ELA_INSIGHTS_ENABLED &&
                      sectionDetail?.subject.name === 'ELA')) && (
                    <Tab
                      icon={<FlagIcon />}
                      label="Goals"
                      id="student-tab-0"
                      aria-controls="student-tabpanel-0"
                      className={styles.studentTab}
                    />
                  )}
                  <Tab
                    icon={<PortfolioIcon />}
                    label="Portfolio"
                    id="student-tab-1"
                    aria-controls="student-tabpanel-1"
                    className={styles.studentTab}
                  />
                </Tabs>

                {activeTabIndex === 0 &&
                  (sectionDetail?.is_math ||
                    (ELA_INSIGHTS_ENABLED &&
                      sectionDetail?.subject.name === 'ELA')) &&
                  studentDetails?.has_goals && (
                    <div className={styles.buttons}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        className={styles.addGoalButton}
                        onClick={handleAddGoalClick}
                      >
                        ADD GOAL
                      </Button>
                      <ExportButton handleClick={handleExportResults} />
                    </div>
                  )}
              </div>

              <Box
                role="tabpanel"
                hidden={activeTabIndex !== 0}
                id="student-tabpanel-0"
                aria-labelledby="student-tab-0"
                p={3}
              >
                {activeTabIndex === 0 &&
                  (sectionDetail?.is_math ||
                    (ELA_INSIGHTS_ENABLED &&
                      sectionDetail?.subject.name === 'ELA')) && (
                    <>
                      {studentDetails?.has_goals && (
                        <GoalsSearchAndFilter
                          studentId={studentId}
                          sectionId={sectionId}
                          onSearchChange={(newValue) => setGoalName(newValue)}
                          value={goalName}
                          progress={filters.progress}
                          standards={filters.standards}
                          onStandardsChange={(newValue) =>
                            setFilters({ ...filters, standards: newValue })
                          }
                          onProgressChange={(newValue) =>
                            setFilters({ ...filters, progress: newValue })
                          }
                          onFiltersReset={handleFiltersReset}
                        />
                      )}
                      <GoalsTable
                        studentId={studentId}
                        sectionId={sectionId}
                        goalName={goalName}
                        filters={filters}
                        hasGoals={studentDetails?.has_goals}
                        standards={filters.standards}
                        onStandardsChange={(newValue) =>
                          setFilters({ ...filters, standards: newValue })
                        }
                        openGoalModal={handleAddGoalClick}
                      />
                    </>
                  )}
              </Box>

              <Box
                role="tabpanel"
                hidden={activeTabIndex !== 1 && sectionDetail?.is_math}
                id="student-tabpanel-1"
                aria-labelledby="student-tab-1"
                p={0}
              >
                {activeTabIndex === 1 &&
                  (sectionDetail?.is_math ||
                    (ELA_INSIGHTS_ENABLED &&
                      sectionDetail?.subject.name === 'ELA'))
                   && (
                  <StudentsPortfolio
                    propStudentId={studentId}
                    history={history}
                    match={match}
                    isEmbedded={true}
                  />
                )}
              </Box>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentDetail;
